import type {
  AutoTopUpRule,
  PackOfferId,
  TopUpSettings,
} from "@pax/api-contract/payments/v1alpha1";
import {
  AnimatedCheck,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  panelCard,
  panelMotion,
} from "@pax/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { api, reachable, requestErrorMessage } from "@/lib/api";
import {
  type AutoTopUpDraft,
  type PackOffer,
  parseThreshold,
  paymentMethodLabel,
  thresholdCredits,
  thresholdFieldError,
} from "../format";
import { refreshCredits, topUpSettingsQuery, useUnderThreshold } from "../queries";
import { CancellationWaiver } from "./CancellationWaiver";

const DEFAULT_THRESHOLD = "3";

/** A paused rule needs the player, so the dialog opens on the tab that shows it. */
export function autoTopUpPaused(settings: TopUpSettings | undefined): boolean {
  return pausedState(settings?.autoState);
}

function pausedState(state: TopUpSettings["autoState"] | undefined): boolean {
  return state === "needs_bank" || state === "needs_address" || state === "declined";
}

export type { AutoTopUpDraft } from "../format";

/** What the dialog footer shows while automatic top-up is being turned on or edited. */
export type AutoTopUpFooterMeta = {
  active: true;
  savingEdits: boolean;
  draft: AutoTopUpDraft | null;
  waitingForStripe: boolean;
  disabled: boolean;
};

/**
 * Automatic top-up, under the pack cards: tick the box, set a threshold, and turn the rule on from
 * the dialog footer without buying a pack.
 */
export function AutoTopUpSection({
  settings,
  packs,
  pickedPack,
  disabled,
  onConfirmWithBank,
  onStartSetup,
  onFooterMetaChange,
  setupWaiting,
  setupMissed,
}: {
  settings: TopUpSettings;
  packs: PackOffer[];
  /** The pack selected in the cards above, which is the one a new rule buys. */
  pickedPack: PackOffer | null;
  disabled: boolean;
  /** Opens Checkout for the rule's pack. Called inside the click, so its tab is allowed. */
  onConfirmWithBank: (offerId: PackOfferId) => void;
  /** Opens Stripe setup for automatic charges. Called inside the click, so its tab is allowed. */
  onStartSetup: (draft: AutoTopUpDraft) => void;
  onFooterMetaChange: (meta: AutoTopUpFooterMeta | null) => void;
  /** The dialog is waiting for a setup tab to turn the rule on. */
  setupWaiting: boolean;
  /** Setup checkout ran but the rule did not turn on in time. */
  setupMissed: boolean;
}) {
  const queryClient = useQueryClient();
  const checkboxId = useId();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<AutoTopUpDraft | null>(null);
  const [resumeWaived, setResumeWaived] = useState(false);
  /** A charge the player is watching just failed: turning the rule on, Try again, or setup. */
  const [chargeFailed, setChargeFailed] = useState(false);
  const previousState = useRef(settings.autoState);
  const [confirmingOff, setConfirmingOff] = useState(false);

  useEffect(() => {
    const was = previousState.current;
    previousState.current = settings.autoState;
    if (settings.autoState === "on") {
      setEditing(false);
      setExpanded(false);
      setChargeFailed(false);
    }
    if (pausedState(settings.autoState) && !pausedState(was)) setChargeFailed(true);
  }, [settings.autoState]);

  const update = useMutation({
    mutationFn: (rule: AutoTopUpRule | null) =>
      reachable(
        rule ? api.payments.autoTopUp.set({ body: rule }) : api.payments.autoTopUp.delete(),
      ),
    onMutate: () => setChargeFailed(false),
    onSuccess: updated => {
      queryClient.setQueryData(topUpSettingsQuery.queryKey, updated);
      void refreshCredits(queryClient);
      if (pausedState(updated.autoState)) setChargeFailed(true);
      setEditing(false);
      setExpanded(false);
      setDraft(null);
    },
  });
  const busy = disabled || update.isPending || setupWaiting;
  const failure = update.error;

  const ruleOn = settings.autoState !== "off";
  const rulePack = packs.find(pack => pack.offerId === settings.autoOfferId);
  const packName = rulePack?.name ?? "your pack";
  const threshold = settings.autoThresholdRaw ? thresholdCredits(settings.autoThresholdRaw) : "";
  const methodLabel = settings.paymentMethod
    ? paymentMethodLabel(settings.paymentMethod)
    : "your payment method";
  const waiverRequired = settings.cancellationWaiverRequired;
  /** The paused rule, as the player turns it back on. Null until they tick the waiver, when it's required. */
  const pausedRule: AutoTopUpDraft | null =
    settings.autoOfferId && settings.autoThresholdRaw && (!waiverRequired || resumeWaived)
      ? {
          autoOfferId: settings.autoOfferId,
          autoThresholdRaw: settings.autoThresholdRaw,
          ...(waiverRequired && { acceptsCancellationWaiver: true as const }),
        }
      : null;
  const resumeWaiver = waiverRequired && (
    <CancellationWaiver
      purchase="auto_top_up"
      checked={resumeWaived}
      disabled={busy}
      onCheckedChange={setResumeWaived}
      className="pl-8"
    />
  );

  const setupPack = editing ? (pickedPack ?? rulePack ?? null) : pickedPack;
  const configuringNewRule = settings.autoState === "off" && expanded;
  const footerActive = configuringNewRule || editing;
  const allowsAutomatic = settings.paymentMethod?.allowsAutomatic === true;

  const footerMeta = useMemo((): AutoTopUpFooterMeta | null => {
    if (!footerActive) return null;
    return {
      active: true,
      savingEdits: editing,
      draft,
      waitingForStripe: setupWaiting,
      disabled: busy || draft === null,
    };
  }, [busy, draft, editing, footerActive, setupWaiting]);

  useEffect(() => {
    onFooterMetaChange(footerMeta);
  }, [footerMeta, onFooterMetaChange]);

  let body: ReactNode = null;
  switch (settings.autoState) {
    case "off":
      body = expanded ? (
        <AutoTopUpForm
          pack={setupPack}
          disabled={busy}
          needsSetup={!allowsAutomatic}
          waiverRequired={waiverRequired}
          onDraftChange={setDraft}
        />
      ) : null;
      break;
    case "on":
      body = editing ? (
        <AutoTopUpForm
          pack={setupPack}
          disabled={busy}
          initialThreshold={threshold}
          needsSetup={!allowsAutomatic}
          waiverRequired={waiverRequired}
          onDraftChange={setDraft}
          onCancel={() => {
            setEditing(false);
            setDraft(null);
          }}
        />
      ) : (
        <Rule
          text={`When you drop below ${threshold} credits, Pax buys ${packName} with ${methodLabel}.`}
          action={
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setEditing(true)}>
              Edit
            </Button>
          }
        />
      );
      break;
    case "charging":
      body = (
        <Rule
          text={`Buying ${packName}…`}
          icon={<RefreshCw aria-hidden className="size-4 shrink-0 animate-spin" />}
        />
      );
      break;
    case "needs_bank":
      body = (
        <Rule
          text={`We need your confirmation to buy ${packName}.`}
          action={
            <Button
              size="sm"
              disabled={busy || !settings.autoOfferId}
              onClick={() => settings.autoOfferId && onConfirmWithBank(settings.autoOfferId)}
            >
              Confirm
            </Button>
          }
        />
      );
      break;
    case "needs_address":
      body = (
        <>
          <Rule
            text="Stripe needs your billing address before it can top you up."
            action={
              <Button
                size="sm"
                disabled={busy || !pausedRule}
                onClick={() => pausedRule && onStartSetup(pausedRule)}
              >
                Continue
              </Button>
            }
          />
          {resumeWaiver}
        </>
      );
      break;
    case "declined":
      body = (
        <>
          <Rule
            text="We couldn't complete your last top-up and removed any credits it added. We've paused automatic top-up."
            action={
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy || !pausedRule}
                  onClick={() => pausedRule && update.mutate(pausedRule)}
                >
                  {update.isPending ? "Trying…" : "Try again"}
                </Button>
                <Button
                  size="sm"
                  disabled={busy || !pausedRule}
                  onClick={() => pausedRule && onStartSetup(pausedRule)}
                >
                  Update payment method
                </Button>
              </div>
            }
          />
          {resumeWaiver}
        </>
      );
      break;
  }

  const checked = ruleOn || expanded;
  const locked = busy || settings.autoState === "charging";

  return (
    <section className={cn(panelCard, "flex flex-col gap-3 p-3")}>
      <label
        htmlFor={checkboxId}
        className={cn(
          "flex cursor-pointer items-start gap-3 rounded-lg has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-foreground/25",
          locked && "cursor-default opacity-60",
          panelMotion,
        )}
      >
        <input
          id={checkboxId}
          type="checkbox"
          className="sr-only"
          checked={checked}
          disabled={locked}
          onChange={() => {
            if (ruleOn) {
              setConfirmingOff(true);
              return;
            }
            setExpanded(!checked);
          }}
        />
        <span className="mt-0.5">
          <AnimatedCheck checked={checked} />
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-medium">Top up automatically</span>
          <span className="text-xs text-muted-foreground">
            When your credits drop below a number you pick, Pax buys a pack for you.
          </span>
        </span>
      </label>
      {body}
      {setupMissed && settings.autoState === "off" && (
        <p role="status" className="text-sm text-muted-foreground">
          Automatic top-up didn't turn on. It needs a payment method, authorized on Stripe.
        </p>
      )}
      {chargeFailed &&
        (settings.autoState === "needs_bank" || settings.autoState === "declined") && (
          <p role="alert" className="pl-8 text-sm text-destructive">
            {settings.autoState === "needs_bank"
              ? `We just tried to buy ${packName}, and your bank wants to confirm the payment first. Nothing was charged.`
              : `We just tried to buy ${packName} with ${methodLabel} and couldn't complete the payment. Nothing was charged.`}
          </p>
        )}
      {failure && (
        <p role="alert" className="text-sm text-destructive">
          {requestErrorMessage(failure)}
        </p>
      )}
      <Dialog open={confirmingOff} onOpenChange={setConfirmingOff}>
        <DialogContent nestedBackdrop>
          <DialogHeader>
            <DialogTitle>Turn off automatic top-up?</DialogTitle>
            <DialogDescription>
              Pax stops buying {packName} when your credits drop below {threshold}. Credits it
              already bought stay in your account.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row flex-wrap justify-end">
            <Button type="button" variant="outline" onClick={() => setConfirmingOff(false)}>
              Keep it on
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setConfirmingOff(false);
                setEditing(false);
                setDraft(null);
                update.mutate(null);
              }}
            >
              Turn off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Rule({ text, icon, action }: { text: string; icon?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3 pl-8 text-sm">
      {icon}
      <p className="min-w-0 flex-1 text-pretty">{text}</p>
      {action}
    </div>
  );
}

function AutoTopUpForm({
  pack,
  disabled,
  initialThreshold = DEFAULT_THRESHOLD,
  needsSetup,
  waiverRequired,
  onDraftChange,
  onCancel,
}: {
  pack: PackOffer | null;
  disabled: boolean;
  initialThreshold?: string;
  needsSetup: boolean;
  /** The player must tick the cancellation waiver before the rule can be turned on. */
  waiverRequired: boolean;
  onDraftChange: (draft: AutoTopUpDraft | null) => void;
  onCancel?: () => void;
}) {
  const thresholdId = useId();
  const [typed, setTyped] = useState(initialThreshold);
  const [waived, setWaived] = useState(false);
  const fieldError = thresholdFieldError(typed, pack);
  const thresholdRaw = parseThreshold(typed, pack);
  const buysNow = useUnderThreshold(thresholdRaw);

  useEffect(() => {
    if (!pack || !thresholdRaw || (waiverRequired && !waived)) {
      onDraftChange(null);
      return;
    }
    onDraftChange({
      autoOfferId: pack.offerId,
      autoThresholdRaw: thresholdRaw,
      ...(waiverRequired && { acceptsCancellationWaiver: true as const }),
    });
  }, [onDraftChange, pack, thresholdRaw, waiverRequired, waived]);

  return (
    <div className="flex flex-col gap-2 pl-8">
      {pack ? (
        <p className="text-sm leading-relaxed text-pretty">
          When my credits drop below{" "}
          <Input
            id={thresholdId}
            inputMode="decimal"
            value={typed}
            disabled={disabled}
            aria-label="Credit threshold"
            aria-invalid={fieldError !== null}
            onChange={event => setTyped(event.target.value)}
            className="mx-0.5 inline-flex h-8 w-[5.5rem] max-w-full shrink-0 px-2 align-middle text-sm"
          />
          {", buy "}
          {pack.name}.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={thresholdId}>When my credits drop below</Label>
          <Input
            id={thresholdId}
            inputMode="decimal"
            value={typed}
            disabled={disabled}
            aria-invalid={fieldError !== null}
            onChange={event => setTyped(event.target.value)}
          />
          <p className="text-sm text-muted-foreground">Pick the pack to buy above.</p>
        </div>
      )}
      {needsSetup && pack && (
        <p className="text-xs text-muted-foreground">
          Turn on auto top-up opens Stripe so you can approve automatic charges on your payment
          method.{" "}
          {buysNow
            ? `You have under ${typed} credits, so Pax buys ${pack.name} as soon as you approve.`
            : "Nothing is charged now."}
        </p>
      )}
      {waiverRequired && (
        <CancellationWaiver
          purchase="auto_top_up"
          checked={waived}
          disabled={disabled}
          onCheckedChange={setWaived}
        />
      )}
      {onCancel && (
        <div>
          <Button variant="outline" size="sm" disabled={disabled} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
      {fieldError && <p className="text-xs text-destructive">{fieldError}</p>}
    </div>
  );
}
